import { CommonModule, DOCUMENT } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  Output,
  Renderer2,
  SimpleChanges,
  inject,
} from '@angular/core';

@Component({
  selector: 'mtm-sash-vertical',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="mtm-sash-vertical__handler" [class.disabled]="!enabled"></div>
    @if (isFullMode) {
      <button
        [class.disabled]="!enabled"
        class="mtm-sash-vertical__caret-btn"
        (click)="resetWidth($event)"
        type="button"
        aria-label="Reset to minimum width"
      >
        <div class="mtm-sash-vertical__toggle-icon">
          <span class="mtm-sash-vertical__toggle-bar"></span>
          <span class="mtm-icon mtm-icon-topic-angle-down-small"></span>
        </div>
      </button>
    }
  `,
  styleUrl: './mtm-sash-vertical.component.scss',
})
export class MtmSashVerticalComponent implements OnChanges {
  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);

  @HostBinding('attr.role') role = 'separator';
  @HostBinding('attr.aria-orientation') orientation = 'vertical';
  @HostBinding('class.mtm-sash-vertical') hostClass = true;
  @HostBinding('class.is-full-mode') get hostIsFullMode(): boolean {
    return this.isFullMode;
  }

  @Input() widthPx: number = 360;
  @Output() widthPxChange = new EventEmitter<number>();
  @Output() isFullModeChange = new EventEmitter<boolean>();
  @Output() resetClicked = new EventEmitter<void>();

  @Input() minPx: number = 200;
  @Input() maxViewportPercent: number = 0.8;
  @Input() resetWidthPx: number | null | undefined = null;
  @Input() enabled: boolean = true;

  private startClientX: number | null = null;
  private startWidthPx: number | null = null;
  private removeFns: Array<() => void> = [];

  @HostListener('mousedown', ['$event'])
  onMousedown(event: MouseEvent) {
    event.preventDefault();
    this.beginResize(event.clientX);
  }

  @HostListener('touchstart', ['$event'])
  onTouchstart(event: TouchEvent) {
    if (!event.touches?.length) return;
    this.beginResize(event.touches[0].clientX);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['widthPx'] && !changes['widthPx'].firstChange) {
      this.emitFullModeChange();
    } else if (changes['widthPx']?.firstChange) {
      setTimeout(() => this.emitFullModeChange(), 0);
    }
  }

  private beginResize(clientX: number) {
    this.startClientX = clientX;
    this.startWidthPx = this.widthPx;
    this.renderer.setStyle(this.document.body, 'user-select', 'none');
    this.renderer.setStyle(this.document.body, '-webkit-user-select', 'none');

    const move = this.renderer.listen(this.document, 'mousemove', (e: MouseEvent) => {
      this.onMove(e.clientX);
    });
    const touchMove = this.renderer.listen(this.document, 'touchmove', (e: TouchEvent) => {
      if (e.touches?.length) this.onMove(e.touches[0].clientX);
    });
    const up = this.renderer.listen(this.document, 'mouseup', () => this.endResize());
    const touchEnd = this.renderer.listen(this.document, 'touchend', () => this.endResize());
    this.removeFns.push(move, touchMove, up, touchEnd);
  }

  private onMove(currentX: number) {
    if (this.startClientX === null || this.startWidthPx === null) return;
    const delta = this.startClientX - currentX;
    const proposed = this.startWidthPx + delta;
    const viewportWidth = this.getViewportWidth();
    const effectiveMax = this.getEffectiveMax(viewportWidth);
    const maxViewportWidth = viewportWidth * this.maxViewportPercent;
    const fullSize = viewportWidth;
    const isShrinking = delta < 0;
    const startedInFullMode = viewportWidth > 0 && Math.abs(this.startWidthPx - fullSize) < 5;

    if (viewportWidth > 0 && !(startedInFullMode && isShrinking)) {
      if (proposed >= maxViewportWidth - 5) {
        this.widthPx = Math.max(this.minPx, fullSize);
        this.widthPxChange.emit(this.widthPx);
        this.emitFullModeChange();
        return;
      }
    }

    let clamped = Math.max(this.minPx, Math.min(effectiveMax, proposed));

    if (startedInFullMode && isShrinking) {
      if (this.resetWidthPx != null && Number.isFinite(this.resetWidthPx) && this.resetWidthPx >= this.minPx) {
        clamped = Math.min(clamped, this.resetWidthPx);
      }
    }

    this.widthPx = clamped;
    this.widthPxChange.emit(this.widthPx);
    this.emitFullModeChange();
  }

  private endResize() {
    this.startClientX = null;
    this.startWidthPx = null;
    this.cleanupListeners();
    this.renderer.removeStyle(this.document.body, 'user-select');
    this.renderer.removeStyle(this.document.body, '-webkit-user-select');
  }

  private cleanupListeners() {
    this.removeFns.forEach((off) => off());
    this.removeFns = [];
  }

  private getEffectiveMax(viewportWidth: number): number {
    return viewportWidth > 0 ? Math.floor(viewportWidth * this.maxViewportPercent) : Number.POSITIVE_INFINITY;
  }

  private getViewportWidth(): number {
    const win = this.document.defaultView as Window | null;
    return win ? win.innerWidth : 0;
  }

  get isFullMode(): boolean {
    const viewportWidth = this.getViewportWidth();
    if (viewportWidth <= 0) return false;
    return Math.abs(this.widthPx - viewportWidth) < 5;
  }

  public resetWidth(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.resetClicked.emit();

    this.widthPx = this.minPx;
    this.widthPxChange.emit(this.widthPx);
    this.emitFullModeChange();
  }

  private emitFullModeChange(): void {
    this.isFullModeChange.emit(this.isFullMode);
  }
}
